import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import { TranslateService } from '@ngx-translate/core';

import {
  SkillDocumentResponse,
  SkillSpecializationResponse,
  SkillAvailableCurator,
  SkillSpecializationCreateRequest,
  TheoryLevelRequest,
  PracticeLevelRequest,
} from '../../../models/skill-learning.model';
import { SkillSpecializationFacadeService } from '../../../services/skill-specialization-facade.service';
import { SkillRoleService } from '../../../services/skill-role.service';
import { ToastService } from '../../../../../../services/toast.service';
import { SidebarService } from '../../../../../../services/side-bar.service';
import { SpecializationFormValue } from '../specialization-form/specialization-form.component';
import { TheoryLevelSavePayload } from '../theory-level-editor/theory-level-editor.component';
import { PracticeLevelSavePayload } from '../practice-level-editor/practice-level-editor.component';

const ROUTE_SPECIALIZATIONS = '/dashboard/specializations';
const LEVEL_THEORY = 1;
const LEVEL_PRACTICE_2 = 2;
const LEVEL_PRACTICE_3 = 3;

export type EditSpecializationPage = 'general' | 'levels';
export interface EditSpecializationMessageParams {
  level?: number;
}

@Component({
  selector: 'app-edit-specialization-container',
  templateUrl: './edit-specialization-container.component.html',
  styleUrls: ['./edit-specialization-container.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditSpecializationContainerComponent implements OnInit, OnDestroy {

  specialization: SkillSpecializationResponse | null = null;
  availableCurators: SkillAvailableCurator[] = [];
  theoryDirectionsPrompt: string = '';
  page: EditSpecializationPage = 'general';
  isLoading: boolean = false;
  isSubmitting: boolean = false;

  private specializationId: string = '';
  private destroy$ = new Subject<void>();

  constructor(
    private facade: SkillSpecializationFacadeService,
    private roleService: SkillRoleService,
    private toastService: ToastService,
    private sidebarService: SidebarService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
  ) {}

  get canManageCurators(): boolean {
    return this.roleService.isHeadOrAdmin();
  }

  get theoryDocumentFileId(): string | null {
    if (!this.specialization || !this.specialization.theoryLevel) {
      return null;
    }
    return this.specialization.theoryLevel.documentFileId;
  }

  get theoryDocument(): SkillDocumentResponse | null {
    if (!this.specialization || !this.specialization.theoryLevel) {
      return null;
    }
    return this.specialization.theoryLevel.document;
  }

  get practice2DocumentFileId(): string | null {
    if (!this.specialization || !this.specialization.practiceLevel2) {
      return null;
    }
    return this.specialization.practiceLevel2.documentFileId;
  }

  get practice2Document(): SkillDocumentResponse | null {
    if (!this.specialization || !this.specialization.practiceLevel2) {
      return null;
    }
    return this.specialization.practiceLevel2.document;
  }

  get practice3DocumentFileId(): string | null {
    if (!this.specialization || !this.specialization.practiceLevel3) {
      return null;
    }
    return this.specialization.practiceLevel3.documentFileId;
  }

  get practice3Document(): SkillDocumentResponse | null {
    if (!this.specialization || !this.specialization.practiceLevel3) {
      return null;
    }
    return this.specialization.practiceLevel3.document;
  }

  ngOnInit(): void {
    this.sidebarService.setSelectedMenuOption('spec-list');
    this.subscribeToSpecializationId();
    this.subscribeToPage();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isGeneralPage(): boolean {
    return this.page === 'general';
  }

  get isLevelsPage(): boolean {
    return this.page === 'levels';
  }

  onGeneralInfoSaved(value: SpecializationFormValue): void {
    const request: SkillSpecializationCreateRequest = {
      name: value.name,
      description: value.description,
    };
    this.isSubmitting = true;
    this.facade.updateSpecializationWithCurators(this.specializationId, request, value.curatorIds)
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.handleSaveSuccess('SKILL_LEARNING_ADMIN_EDIT_SPECIALIZATION_UPDATED'),
        () => this.handleSaveError(),
      );
  }

  onCuratorsChanged(curatorIds: string[]): void {
    this.isSubmitting = true;
    this.facade.assignCurators(this.specializationId, curatorIds)
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.handleSaveSuccess('SKILL_LEARNING_ADMIN_EDIT_CURATORS_UPDATED'),
        () => this.handleSaveError(),
      );
  }

  onTheoryLevelSaved(payload: TheoryLevelSavePayload): void {
    this.isSubmitting = true;
    this.facade.updateTheoryLevel(this.specializationId, payload.request)
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.handleTheoryLevelSavedSuccess(payload.documentFile),
        () => this.handleSaveError(),
      );
  }

  onPracticeLevel2Saved(payload: PracticeLevelSavePayload): void {
    this.isSubmitting = true;
    this.facade.updatePracticeLevel2(this.specializationId, payload.request)
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.handlePracticeLevelSavedSuccess(payload.documentFile, LEVEL_PRACTICE_2),
        () => this.handleSaveError(),
      );
  }

  onPracticeLevel3Saved(payload: PracticeLevelSavePayload): void {
    this.isSubmitting = true;
    this.facade.updatePracticeLevel3(this.specializationId, payload.request)
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.handlePracticeLevelSavedSuccess(payload.documentFile, LEVEL_PRACTICE_3),
        () => this.handleSaveError(),
      );
  }

  onDocumentDownload(level: number): void {
    const url = this.facade.buildDocumentDownloadUrl(this.specializationId, level);
    window.open(url, '_blank');
  }

  onCancelRequested(): void {
    this.router.navigate([ROUTE_SPECIALIZATIONS]);
  }

  private subscribeToSpecializationId(): void {
    this.route.params
      .takeUntil(this.destroy$)
      .subscribe(params => this.handleSpecializationIdChange(params['id']));
  }

  private subscribeToPage(): void {
    this.route.url
      .takeUntil(this.destroy$)
      .subscribe(segments => this.handlePageChange(segments));
  }

  private handleSpecializationIdChange(id: string): void {
    if (!id || this.specializationId === id) {
      return;
    }
    this.specializationId = id;
    this.theoryDirectionsPrompt = '';
    this.loadSpecialization();
  }

  private handlePageChange(segments: UrlSegment[]): void {
    const page = this.resolvePage(segments);
    if (this.page === page) {
      return;
    }
    this.page = page;
    this.cdr.markForCheck();
  }

  private resolvePage(segments: UrlSegment[]): EditSpecializationPage {
    if (!segments || segments.length === 0) {
      return 'general';
    }
    return segments[segments.length - 1].path === 'levels' ? 'levels' : 'general';
  }

  private handleTheoryLevelSavedSuccess(documentFile: File | null): void {
    if (documentFile) {
      this.uploadDocument(documentFile, LEVEL_THEORY);
      return;
    }
    this.handleSaveSuccess('SKILL_LEARNING_ADMIN_EDIT_LEVEL_SAVED', { level: LEVEL_THEORY });
    this.loadSpecialization();
  }

  private handlePracticeLevelSavedSuccess(documentFile: File | null, level: number): void {
    if (documentFile) {
      this.uploadDocument(documentFile, level);
      return;
    }
    this.handleSaveSuccess('SKILL_LEARNING_ADMIN_EDIT_LEVEL_SAVED', { level: level });
    this.loadSpecialization();
  }

  private uploadDocument(file: File, level: number): void {
    this.facade.uploadLevelDocument(this.specializationId, level, file)
      .takeUntil(this.destroy$)
      .subscribe(
        () => {
          this.handleSaveSuccess('SKILL_LEARNING_ADMIN_EDIT_LEVEL_SAVED', { level: level });
          this.loadSpecialization();
        },
        () => {
          this.toastService.error(this.translate.instant(
            'SKILL_LEARNING_ADMIN_EDIT_LEVEL_DOCUMENT_UPLOAD_ERROR',
            { level: level },
          ));
          this.isSubmitting = false;
          this.cdr.markForCheck();
        },
      );
  }

  private loadSpecialization(): void {
    this.isLoading = true;
    this.facade.fetchSpecializationDetail(this.specializationId)
      .takeUntil(this.destroy$)
      .subscribe(
        spec => this.handleSpecializationLoaded(spec),
        () => this.handleLoadError(),
      );
  }

  private loadAvailableCurators(): void {
    this.facade.fetchAllCuratorsForEdit()
      .takeUntil(this.destroy$)
      .subscribe(
        curators => {
          this.availableCurators = curators;
          this.cdr.markForCheck();
        },
        () => this.cdr.markForCheck(),
      );
  }

  private loadTheoryDirectionsPrompt(): void {
    this.facade.fetchTheoryDirectionsPrompt(this.specializationId)
      .takeUntil(this.destroy$)
      .subscribe(
        prompt => {
          this.theoryDirectionsPrompt = prompt.text || '';
          this.cdr.markForCheck();
        },
        () => {
          this.theoryDirectionsPrompt = '';
          this.cdr.markForCheck();
        },
      );
  }

  private handleSpecializationLoaded(spec: SkillSpecializationResponse): void {
    this.specialization = spec;
    this.isLoading = false;
    this.cdr.markForCheck();
    this.loadAvailableCurators();
    this.loadTheoryDirectionsPrompt();
  }

  private handleLoadError(): void {
    this.isLoading = false;
    this.toastService.error(this.translate.instant('SKILL_LEARNING_ADMIN_EDIT_LOAD_ERROR'));
    this.cdr.markForCheck();
  }

  private handleSaveSuccess(message: string, params?: EditSpecializationMessageParams): void {
    this.isSubmitting = false;
    this.toastService.success(this.translate.instant(message, params));
    this.cdr.markForCheck();
  }

  private handleSaveError(): void {
    this.isSubmitting = false;
    this.toastService.error(this.translate.instant('SKILL_LEARNING_ADMIN_EDIT_SAVE_ERROR'));
    this.cdr.markForCheck();
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/edit-specialization-container/edit-specialization-container.component.ts